import { NavLink, Navigate, Outlet } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Activity, Dumbbell, Ellipsis, House, Utensils, type LucideIcon } from 'lucide-react';
import { UpdatePrompt } from '@/features/pwa/UpdatePrompt';
import { StandaloneNotice } from '@/features/pwa/StandaloneNotice';
import { BackupBanner } from '@/features/backup/BackupBanner';
import { leerSettings } from '@/db/schema';
import { Encabezado } from './Encabezado';

const NAVEGACION: ReadonlyArray<{ ruta: string; titulo: string; icono: LucideIcon }> = [
  { ruta: '/', titulo: 'Hoy', icono: House },
  { ruta: '/gym', titulo: 'Gym', icono: Dumbbell },
  { ruta: '/comida', titulo: 'Comida', icono: Utensils },
  { ruta: '/cuerpo', titulo: 'Cuerpo', icono: Activity },
  { ruta: '/mas', titulo: 'Más', icono: Ellipsis },
];

/**
 * Armazón de la app (design.md, secciones 12.1 y 12.5).
 *
 * Diseño Foco: encabezado con la marca, el sol y la luna y el perfil; una sola
 * columna; y la barra inferior de cinco pestañas al alcance del pulgar
 * (Req. 8.1, 8.6), con las áreas seguras del iPhone. Los avisos de versión
 * nueva, de almacenamiento separado y de respaldo viven aquí para que se vean
 * desde cualquier pantalla.
 *
 * Origen: Control de Negocio de Comida.
 */
export function Layout() {
  const settings = useLiveQuery(() => leerSettings(), []);

  // La primera vez se piden los datos básicos del perfil (Req. 4.1).
  if (settings !== undefined && !settings.primerosPasosHechos) {
    return <Navigate to="/primeros-pasos" replace />;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-fondo">
      {/*
        Botón y no enlace "#contenido": con el enrutado por fragmento, cambiar
        el # llevaría a otra pantalla en lugar de saltar al contenido.
      */}
      <button
        type="button"
        onClick={() => document.getElementById('contenido')?.focus()}
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-xl focus:bg-superficie focus:px-3 focus:py-2"
      >
        Ir al contenido
      </button>

      <div className="mx-auto w-full max-w-lg pt-segura">
        <Encabezado />
      </div>

      <main
        id="contenido"
        tabIndex={-1}
        className="mx-auto w-full max-w-lg flex-1 px-[18px] pb-[calc(7rem+env(safe-area-inset-bottom))] pt-[11px] outline-none min-[401px]:px-[23px]"
      >
        <div className="mb-5 space-y-3 empty:hidden">
          <UpdatePrompt />
          <StandaloneNotice />
          <BackupBanner />
        </div>

        <Outlet />
      </main>

      <nav
        aria-label="Navegación principal"
        className="fixed inset-x-0 bottom-0 z-10 border-t border-borde bg-superficie"
      >
        <ul className="mx-auto grid w-full max-w-lg grid-cols-5 gap-[3px] px-3 pb-[max(15px,env(safe-area-inset-bottom))] pt-[11px]">
          {NAVEGACION.map(({ ruta, titulo, icono: Icono }) => (
            <li key={ruta} className="min-w-0">
              <NavLink
                to={ruta}
                end={ruta === '/'}
                className={({ isActive }) =>
                  [
                    'flex min-h-[54px] flex-col items-center justify-center gap-[5px] rounded-[15px] px-0.5 py-[7px] text-[11px]',
                    isActive ? 'bg-marca-suave text-marca' : 'text-texto-suave',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <Icono className="size-5" />
                    <span>{titulo}</span>
                    {/* La pestaña actual también se distingue por su fondo (Req. 8.5) */}
                    <span className="sr-only">{isActive ? '(sección actual)' : ''}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
